/**
 * Education Transform Utility
 *
 * Transforms between frontend study selection state and backend educations[] format.
 * Production-ready with complete bidirectional transformation support.
 *
 * Frontend state (from useStudySelection):
 * {
 *   selectedMBOLevels: {"Niveau 3": true, "Niveau 4": true},
 *   selectedMBOStudies: {"Niveau 3-Techniek-Elektricien": true},
 *   selectedMBOYears: {"Niveau 3-Jaar 3": true},
 *   selectedHBOLevels: {"HBO Bachelor": true},
 *   selectedHBOStudies: {"HBO Bachelor-Economie-Finance & Control": true},
 *   selectedHBOYears: {"HBO Bachelor-Jaar 3": true},
 *   ...
 * }
 *
 * Backend format:
 * {
 *   educations: [
 *     { levelId: "HBO_BACHELOR", studyIds: ["HBO_BACHELOR_FINANCE_AND_CONTROL"], years: [3] }
 *   ]
 * }
 */

import {
  LEVEL_IDS,
  LEVEL_NAMES,
  generateStudyId,
  getLevelName,
  parseStudyId
} from '../data/educationMappings';

// Import study data from constants for reverse lookup
import {
  MBO_NIVEAU_1_STUDIES,
  MBO_NIVEAU_2_STUDIES,
  MBO_NIVEAU_3_STUDIES,
  MBO_NIVEAU_4_STUDIES,
  HBO_BACHELOR_STUDIES,
  HBO_ASSOCIATE_DEGREE_STUDIES,
  HBO_MASTER_STUDIES,
  UNIVERSITEIT_BACHELOR_STUDIES,
  UNIVERSITEIT_MASTER_STUDIES
} from '../features/vacature-plaatsen/data/constants';

// ============================================================================
// Study ID Reverse Lookup Cache
// ============================================================================

/**
 * Cache for study ID → display info mapping
 * Built once on first use for performance
 */
let studyIdLookupCache = null;

const LEVEL_STUDY_DATA = {
  'Niveau 1': { data: MBO_NIVEAU_1_STUDIES, isFlat: true },
  'Niveau 2': { data: MBO_NIVEAU_2_STUDIES, isFlat: false },
  'Niveau 3': { data: MBO_NIVEAU_3_STUDIES, isFlat: false },
  'Niveau 4': { data: MBO_NIVEAU_4_STUDIES, isFlat: false },
  'HBO Bachelor': { data: HBO_BACHELOR_STUDIES, isFlat: false },
  'HBO Associate Degree': { data: HBO_ASSOCIATE_DEGREE_STUDIES, isFlat: false },
  'HBO Master': { data: HBO_MASTER_STUDIES, isFlat: false },
  'Universiteit Bachelor': { data: UNIVERSITEIT_BACHELOR_STUDIES, isFlat: false },
  'Universiteit Master': { data: UNIVERSITEIT_MASTER_STUDIES, isFlat: false }
};

function collectStudyNames(data, isFlat) {
  if (!data) return [];
  if (isFlat && Array.isArray(data)) return data;

  const studies = [];
  for (const categoryData of Object.values(data)) {
    if (Array.isArray(categoryData)) {
      studies.push(...categoryData);
    } else if (categoryData && typeof categoryData === 'object') {
      for (const subcategoryStudies of Object.values(categoryData)) {
        if (Array.isArray(subcategoryStudies)) studies.push(...subcategoryStudies);
      }
    }
  }
  return studies;
}

/**
 * Build reverse lookup map from study ID to display information
 * This is cached for performance
 *
 * @returns {Map<string, {levelName: string, category: string, studyName: string, key: string}>}
 */
function buildStudyIdLookup() {
  if (studyIdLookupCache) return studyIdLookupCache;

  studyIdLookupCache = new Map();

  // Configuration for each level
  const levelConfigs = [
    { levelName: 'Niveau 1', levelId: 'MBO_NIVEAU_1', data: MBO_NIVEAU_1_STUDIES, isFlat: true },
    { levelName: 'Niveau 2', levelId: 'MBO_NIVEAU_2', data: MBO_NIVEAU_2_STUDIES, isFlat: false },
    { levelName: 'Niveau 3', levelId: 'MBO_NIVEAU_3', data: MBO_NIVEAU_3_STUDIES, isFlat: false },
    { levelName: 'Niveau 4', levelId: 'MBO_NIVEAU_4', data: MBO_NIVEAU_4_STUDIES, isFlat: false },
    { levelName: 'HBO Bachelor', levelId: 'HBO_BACHELOR', data: HBO_BACHELOR_STUDIES, isFlat: false },
    { levelName: 'HBO Associate Degree', levelId: 'HBO_ASSOCIATE_DEGREE', data: HBO_ASSOCIATE_DEGREE_STUDIES, isFlat: false },
    { levelName: 'HBO Master', levelId: 'HBO_MASTER', data: HBO_MASTER_STUDIES, isFlat: false },
    { levelName: 'Universiteit Bachelor', levelId: 'UNIVERSITEIT_BACHELOR', data: UNIVERSITEIT_BACHELOR_STUDIES, isFlat: false },
    { levelName: 'Universiteit Master', levelId: 'UNIVERSITEIT_MASTER', data: UNIVERSITEIT_MASTER_STUDIES, isFlat: false }
  ];

  for (const config of levelConfigs) {
    if (!config.data) continue;

    if (config.isFlat) {
      // MBO Niveau 1 has a flat array
      for (const studyName of config.data) {
        const studyId = generateStudyId(config.levelId, studyName);
        if (studyId) {
          studyIdLookupCache.set(studyId, {
            levelName: config.levelName,
            category: null,
            studyName,
            key: `${config.levelName}-${studyName}`
          });
        }
      }
    } else {
      // Others have nested category → subcategory → studies structure
      for (const [category, subcategories] of Object.entries(config.data)) {
        if (Array.isArray(subcategories)) {
          // Simple category → [studies] structure (some HBO Associate Degree)
          for (const studyName of subcategories) {
            const studyId = generateStudyId(config.levelId, studyName);
            if (studyId) {
              studyIdLookupCache.set(studyId, {
                levelName: config.levelName,
                category,
                studyName,
                key: `${config.levelName}-${category}-${studyName}`
              });
            }
          }
        } else {
          // Nested category → subcategory → [studies] structure
          for (const [subcategory, studies] of Object.entries(subcategories)) {
            if (Array.isArray(studies)) {
              for (const studyName of studies) {
                const studyId = generateStudyId(config.levelId, studyName);
                if (studyId) {
                  studyIdLookupCache.set(studyId, {
                    levelName: config.levelName,
                    category: subcategory,
                    studyName,
                    key: `${config.levelName}-${subcategory}-${studyName}`
                  });
                }
              }
            }
          }
        }
      }
    }
  }

  console.log(`✅ Built study ID lookup cache with ${studyIdLookupCache.size} entries`);
  return studyIdLookupCache;
}

/**
 * Look up study information by study ID
 *
 * @param {string} studyId - Study ID (e.g., "HBO_BACHELOR_FINANCE_AND_CONTROL")
 * @returns {Object|null} Study info or null if not found
 */
export function lookupStudyById(studyId) {
  const lookup = buildStudyIdLookup();
  return lookup.get(studyId) || null;
}

/**
 * Get display name for a study ID
 *
 * @param {string} studyId - Study ID
 * @returns {string} Display name or the study ID if not found
 */
export function getStudyDisplayName(studyId) {
  const info = lookupStudyById(studyId);
  return info ? info.studyName : studyId;
}

// ============================================================================
// Frontend → Backend Transformation
// ============================================================================

/**
 * Transform frontend study selection state to backend educations[] format
 *
 * @param {Object} studyState - Frontend study selection state
 * @returns {Array} educations array for backend
 */
export function transformToEducations(studyState) {
  const educations = [];

  // MBO level name mapping (frontend uses "Niveau X", backend expects "MBO Niveau X")
  const mboLevelMapping = {
    'Niveau 1': 'MBO Niveau 1',
    'Niveau 2': 'MBO Niveau 2',
    'Niveau 3': 'MBO Niveau 3',
    'Niveau 4': 'MBO Niveau 4'
  };

  // Process each education type
  const typeConfigs = [
    {
      type: 'MBO',
      levelsKey: 'selectedMBOLevels',
      studiesKey: 'selectedMBOStudies',
      yearsKey: 'selectedMBOYears',
      levelMapping: mboLevelMapping
    },
    {
      type: 'HBO',
      levelsKey: 'selectedHBOLevels',
      studiesKey: 'selectedHBOStudies',
      yearsKey: 'selectedHBOYears',
      levelMapping: null
    },
    {
      type: 'Uni',
      levelsKey: 'selectedUniLevels',
      studiesKey: 'selectedUniStudies',
      yearsKey: 'selectedUniYears',
      levelMapping: null
    }
  ];

  for (const config of typeConfigs) {
    const levels = studyState[config.levelsKey] || {};
    const studies = studyState[config.studiesKey] || {};
    const years = studyState[config.yearsKey] || {};

    // Get selected levels for this type
    const selectedLevelNames = Object.keys(levels).filter(k => levels[k]);

    for (const levelNameRaw of selectedLevelNames) {
      // Map level name if needed (MBO uses short form in frontend)
      const levelName = config.levelMapping?.[levelNameRaw] || levelNameRaw;
      const levelId = LEVEL_IDS[levelName];

      if (!levelId) {
        console.warn(`Unknown level name: ${levelName} (raw: ${levelNameRaw})`);
        continue;
      }

      // Extract studies for this level
      // Study keys can be:
      // - MBO: "Niveau 3-Subcategory-StudyName"
      // - HBO/Uni: "HBO Bachelor-Category-StudyName"
      const studyIds = Object.keys(studies)
        .filter(key => studies[key] && key.startsWith(levelNameRaw))
        .map(key => {
          const studyName = extractStudyName(key, levelNameRaw);
          return studyName ? generateStudyId(levelId, studyName) : null;
        })
        .filter(Boolean);

      // Extract years for this level
      // Year keys: "Niveau 3-Jaar 3" or "HBO Bachelor-Jaar 3"
      const yearNumbers = Object.keys(years)
        .filter(key => years[key] && key.startsWith(levelNameRaw))
        .map(key => {
          const yearMatch = key.match(/Jaar\s*(\d+)/i);
          return yearMatch ? parseInt(yearMatch[1], 10) : null;
        })
        .filter(n => n !== null && n >= 1 && n <= 6)
        .sort((a, b) => a - b);

      // Only add if level is selected (studyIds and years can be empty = "all welcome")
      educations.push({
        levelId,
        studyIds,
        years: yearNumbers
      });
    }
  }

  return educations;
}

/**
 * Extract study name from a study key
 *
 * @param {string} key - Study key (e.g., "HBO Bachelor-Economie-Finance & Control")
 * @param {string} levelName - Level name (e.g., "HBO Bachelor" or "Niveau 3")
 * @returns {string|null} Study name
 */
function extractStudyName(key, levelName) {
  if (!key || !levelName) return null;

  // Remove level prefix
  const withoutLevel = key.replace(`${levelName}-`, '');

  const config = LEVEL_STUDY_DATA[levelName];
  if (config) {
    const knownStudy = collectStudyNames(config.data, config.isFlat)
      .sort((a, b) => b.length - a.length)
      .find((studyName) => withoutLevel === studyName || withoutLevel.endsWith(`-${studyName}`));

    if (knownStudy) return knownStudy;
  }

  // The study name is the last part after the category/subcategory
  // e.g., "Economie-Finance & Control" → "Finance & Control"
  // e.g., "Subcategory-StudyName" → "StudyName"
  const parts = withoutLevel.split('-');

  // For MBO Niveau 1, there's no category, just "Niveau 1-StudyName"
  if (levelName === 'Niveau 1') {
    return withoutLevel;
  }

  // For others, take the last part
  if (parts.length >= 1) {
    return parts[parts.length - 1];
  }

  return withoutLevel;
}

// ============================================================================
// Backend → Frontend Transformation (for editing)
// ============================================================================

/**
 * Transform backend educations[] back to frontend state format
 * Used when editing an existing vacancy
 *
 * @param {Array} educations - Backend educations array
 * @returns {Object} Frontend study selection state
 */
export function transformToStudyState(educations) {
  const state = {
    selectedMBOLevels: {},
    selectedMBOStudies: {},
    selectedMBOYears: {},
    selectedHBOLevels: {},
    selectedHBOStudies: {},
    selectedHBOYears: {},
    selectedUniLevels: {},
    selectedUniStudies: {},
    selectedUniYears: {}
  };

  if (!educations || !Array.isArray(educations)) {
    return state;
  }

  // Build the lookup cache if not already built
  buildStudyIdLookup();

  // Reverse mapping for MBO levels (backend uses "MBO Niveau X", frontend uses "Niveau X")
  const mboLevelReverseMapping = {
    'MBO Niveau 1': 'Niveau 1',
    'MBO Niveau 2': 'Niveau 2',
    'MBO Niveau 3': 'Niveau 3',
    'MBO Niveau 4': 'Niveau 4'
  };

  for (const edu of educations) {
    const levelNameFull = getLevelName(edu.levelId);
    if (!levelNameFull) continue;

    // Determine education type and frontend level name
    let type, levelName;
    if (edu.levelId.startsWith('MBO')) {
      type = 'MBO';
      levelName = mboLevelReverseMapping[levelNameFull] || levelNameFull;
    } else if (edu.levelId.startsWith('HBO')) {
      type = 'HBO';
      levelName = levelNameFull;
    } else if (edu.levelId.startsWith('UNIVERSITEIT')) {
      type = 'Uni';
      levelName = levelNameFull;
    } else {
      continue;
    }

    // Set level as selected
    state[`selected${type}Levels`][levelName] = true;

    // Set studies as selected
    if (edu.studyIds && edu.studyIds.length > 0) {
      for (const studyId of edu.studyIds) {
        const studyInfo = lookupStudyById(studyId);
        if (studyInfo) {
          // Build the frontend key
          let studyKey;
          if (type === 'MBO') {
            // MBO uses "Niveau X-Category-StudyName" format
            studyKey = studyInfo.category
              ? `${levelName}-${studyInfo.category}-${studyInfo.studyName}`
              : `${levelName}-${studyInfo.studyName}`;
          } else {
            // HBO/Uni uses "Level-Category-StudyName" format
            studyKey = studyInfo.category
              ? `${levelName}-${studyInfo.category}-${studyInfo.studyName}`
              : `${levelName}-${studyInfo.studyName}`;
          }
          state[`selected${type}Studies`][studyKey] = true;
        } else {
          // Fallback: store with parsed info
          const parsed = parseStudyId(studyId);
          if (parsed) {
            const studyKey = `${levelName}-Unknown-${parsed.normalizedName}`;
            state[`selected${type}Studies`][studyKey] = true;
          }
        }
      }
    }

    // Set years as selected
    if (edu.years && edu.years.length > 0) {
      for (const year of edu.years) {
        const yearKey = `${levelName}-Jaar ${year}`;
        state[`selected${type}Years`][yearKey] = true;
      }
    }
  }

  return state;
}

// ============================================================================
// Display Transformation
// ============================================================================

/**
 * Transform backend educations[] to frontend display format
 * Used when displaying vacancy education requirements
 *
 * @param {Array} educations - Backend educations array
 * @returns {Object} Display-friendly education data
 */
export function transformForDisplay(educations) {
  if (!educations || !Array.isArray(educations) || educations.length === 0) {
    return {
      levels: [],
      description: 'Alle studenten welkom',
      details: []
    };
  }

  // Build lookup for study names
  buildStudyIdLookup();

  const levels = [];
  const descriptions = [];
  const details = [];

  for (const edu of educations) {
    const levelName = getLevelName(edu.levelId);
    if (!levelName) continue;

    levels.push(levelName);

    // Get study names
    const studyNames = (edu.studyIds || [])
      .map(id => getStudyDisplayName(id))
      .filter(Boolean);

    // Build detail object
    const detail = {
      level: levelName,
      studies: studyNames,
      years: edu.years || [],
      allStudiesWelcome: !studyNames.length,
      allYearsWelcome: !(edu.years && edu.years.length)
    };
    details.push(detail);

    // Build description string
    const parts = [levelName];
    if (studyNames.length > 0) {
      if (studyNames.length <= 3) {
        parts.push(studyNames.join(', '));
      } else {
        parts.push(`${studyNames.length} studies`);
      }
    } else {
      parts.push('Alle studies');
    }

    if (edu.years && edu.years.length > 0) {
      parts.push(`Jaar ${edu.years.join(', ')}`);
    } else {
      parts.push('Alle jaren');
    }

    descriptions.push(parts.join(' - '));
  }

  return {
    levels: [...new Set(levels)],
    description: descriptions.join(' | '),
    details
  };
}

// ============================================================================
// Validation
// ============================================================================

/**
 * Validate educations array structure
 *
 * @param {Array} educations - Educations array to validate
 * @returns {Object} { valid: boolean, errors: string[] }
 */
export function validateEducations(educations) {
  const errors = [];

  if (!educations) {
    return { valid: true, errors: [] }; // null/undefined is valid (no requirements)
  }

  if (!Array.isArray(educations)) {
    return { valid: false, errors: ['educations must be an array'] };
  }

  for (let i = 0; i < educations.length; i++) {
    const edu = educations[i];

    if (!edu.levelId) {
      errors.push(`educations[${i}]: missing levelId`);
    } else if (!LEVEL_NAMES[edu.levelId]) {
      errors.push(`educations[${i}]: invalid levelId "${edu.levelId}"`);
    }

    if (edu.studyIds && !Array.isArray(edu.studyIds)) {
      errors.push(`educations[${i}]: studyIds must be an array`);
    }

    if (edu.years && !Array.isArray(edu.years)) {
      errors.push(`educations[${i}]: years must be an array`);
    } else if (edu.years) {
      for (const year of edu.years) {
        if (typeof year !== 'number' || year < 1 || year > 6) {
          errors.push(`educations[${i}]: invalid year "${year}" (must be 1-6)`);
        }
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Check if study state has any selections
 *
 * @param {Object} studyState - Frontend study state
 * @returns {boolean} True if any education selections are made
 */
export function hasEducationSelections(studyState) {
  const levelKeys = ['selectedMBOLevels', 'selectedHBOLevels', 'selectedUniLevels'];

  for (const key of levelKeys) {
    const levels = studyState[key] || {};
    if (Object.values(levels).some(Boolean)) {
      return true;
    }
  }

  return false;
}

export default {
  transformToEducations,
  transformToStudyState,
  transformForDisplay,
  validateEducations,
  hasEducationSelections,
  lookupStudyById,
  getStudyDisplayName
};
